import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deleteIncidencia, getIncidencias, createIncidencia, getComunidades } from '../services/api';
import FileDropZone from '../components/FileDropZone';
import { useToast } from '../components/Toast';
import Pagination from '../components/Pagination';
import DateInput from '../components/DateInput';
import Button from '../components/ui/Button';
import EmptyState from '../components/EmptyState';
import './IncidenciasRedisenio.css';

function Incidencias({ user }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [incidencias, setIncidencias] = useState([]);
  const [total, setTotal] = useState(0);
  const [filtros, setFiltros] = useState(() => {
    try {
      const saved = sessionStorage.getItem('inc_filtros');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      estado: [],
      categoria: '',
      prioridad: '',
      fechaDesde: '',
      fechaHasta: '',
      busqueda: searchParams.get('search') || '',
      filtroComunidad: ''
    };
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('inc_page') || '1'); } catch { return 1; }
  });
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('inc_advanced') || 'false'); } catch { return false; }
  });
  const [showModal, setShowModal] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [comunidades, setComunidades] = useState([]);
  const [newFormData, setNewFormData] = useState({ titulo: '', descripcion: '', categoria: 'Otros', prioridad: 'media', ubicacion: '', comunidad_id: '' });
  const [imagenes, setImagenes] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const filtrosInicializados = useRef(false);

  // Revocar object URLs al cambiar la lista de previews o al desmontar el componente,
  // evitando fugas de memoria en sesiones con múltiples subidas de imágenes.
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Carga inicial de comunidades (solo admin)
  useEffect(() => {
    if (user?.rol === 'administrador') {
      getComunidades().then(res => setComunidades(res.data)).catch(console.error);
    }
  }, []);

  // Fetch server-side con paginación y filtros.
  // Primera carga: inmediata (restaurando sessionStorage).
  // Cambios posteriores: debounce 300 ms para no disparar una petición por cada tecla.
  useEffect(() => {
    if (!filtrosInicializados.current) {
      filtrosInicializados.current = true;
      cargarIncidencias();
      return;
    }
    const timer = setTimeout(cargarIncidencias, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, currentPage, itemsPerPage]);

  const cargarIncidencias = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, per_page: itemsPerPage };

      // Estado: por defecto excluir resueltas salvo que haya búsqueda o filtro explícito
      if (filtros.estado.length > 0) {
        params.estado = filtros.estado.join(',');
      } else if (!filtros.busqueda) {
        params.estado = 'pendiente,en_proceso';
      }

      if (filtros.busqueda)        params.q            = filtros.busqueda;
      if (filtros.categoria)       params.categoria    = filtros.categoria;
      if (filtros.prioridad)       params.prioridad    = filtros.prioridad;
      if (filtros.fechaDesde)      params.fecha_desde  = filtros.fechaDesde;
      if (filtros.fechaHasta)      params.fecha_hasta  = filtros.fechaHasta;
      if (filtros.filtroComunidad) params.comunidad_id = filtros.filtroComunidad;

      const response = await getIncidencias(params);
      setIncidencias(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Actualiza filtros y vuelve a la página 1 (React 18 agrupa ambos setters → un solo render)
  const updateFiltros = (newFiltros) => {
    setFiltros(newFiltros);
    setCurrentPage(1);
  };

  const toggleFilterValue = (filterName, value) => {
    const arr = filtros[filterName];
    const newArr = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
    updateFiltros({ ...filtros, [filterName]: newArr });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewFormData({ titulo: '', descripcion: '', categoria: 'Otros', prioridad: 'media', ubicacion: '', comunidad_id: '' });
    setImagenes([]);
    setPreviewUrls([]);
  };

  const handleNewFormChange = (e) => {
    const { name, value } = e.target;
    setNewFormData({ ...newFormData, [name]: value });
  };

  const handleFilesSelected = (newFiles) => {
    const all = [...imagenes, ...newFiles];
    const limited = all.slice(0, 5);
    if (all.length > 5) toast.warning('Máximo 5 imágenes');
    setImagenes(limited);
    setPreviewUrls(limited.map(f => URL.createObjectURL(f)));
  };

  const handleImagenesChange = (e) => {
    handleFilesSelected(Array.from(e.target.files));
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImagenes(imagenes.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  const handleSubmitIncidencia = async (e) => {
    e.preventDefault();
    setLoadingSave(true);
    try {
      const fd = new FormData();
      Object.keys(newFormData).forEach(key => { if (newFormData[key]) fd.append(key, newFormData[key]); });
      imagenes.forEach(img => fd.append('imagenes', img));
      await createIncidencia(fd);
      toast.success('¡Incidencia creada!');
      handleCloseModal();
      cargarIncidencias();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear incidencia.');
    } finally {
      setLoadingSave(false);
    }
  };

  const handleEliminar = (e, incidencia) => {
    e.stopPropagation();

    // Optimistic: remove from UI immediately
    setIncidencias(prev => prev.filter(i => i.id !== incidencia.id));

    let undone = false;

    toast.undo('Incidencia eliminada', () => {
      undone = true;
      setIncidencias(prev => {
        // Reinsert in original position (sorted by id desc)
        const updated = [...prev, incidencia];
        updated.sort((a, b) => b.id - a.id);
        return updated;
      });
    }, 5000);

    setTimeout(async () => {
      if (undone) return;
      try {
        await deleteIncidencia(incidencia.id);
      } catch (error) {
        // Real delete failed — restore item
        setIncidencias(prev => {
          const updated = [...prev, incidencia];
          updated.sort((a, b) => b.id - a.id);
          return updated;
        });
        toast.error('Error al eliminar la incidencia');
      }
    }, 5100);
  };

  const handlePostClick = (incidencia) => {
    // Guardar estado actual para restaurar al volver
    sessionStorage.setItem('inc_filtros', JSON.stringify(filtros));
    sessionStorage.setItem('inc_page', JSON.stringify(currentPage));
    sessionStorage.setItem('inc_advanced', JSON.stringify(showAdvancedFilters));
    navigate(`/incidencias/${incidencia.id}`);
  };

  const handlePostKeyDown = (event, incidencia) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePostClick(incidencia);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { text: 'Pendiente' },
      en_proceso: { text: 'En proceso' },
      resuelta: { text: 'Resuelta' }
    };
    return badges[estado] || { text: 'Otro' };
  };

  const getCategoriaIcon = (categoria) => {
    const props = { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
    const icons = {
      'Fontanería': (
        <svg {...props}>
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
        </svg>
      ),
      'Electricidad': (
        <svg {...props}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      ),
      'Ascensor': (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M8 9l4-4 4 4" />
          <path d="M16 15l-4 4-4-4" />
        </svg>
      ),
      'Limpieza': (
        <svg {...props}>
          <path d="M12 3c-1 2-2 3.5-2 5a2 2 0 0 0 4 0c0-1.5-1-3-2-5z" />
          <path d="M5 12c-1 2-2 3.5-2 5a2 2 0 0 0 4 0c0-1.5-1-3-2-5z" />
          <path d="M19 12c-1 2-2 3.5-2 5a2 2 0 0 0 4 0c0-1.5-1-3-2-5z" />
          <path d="M5 8h14" />
        </svg>
      ),
      'Seguridad': (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      'Otros': (
        <svg {...props}>
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      ),
    };
    return icons[categoria] || icons['Otros'];
  };

  const puedeEliminar = (incidencia) => {
    return user.rol === 'administrador' || user.rol === 'presidente' || incidencia.usuario_id === user.id;
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const tieneFiltrosActivos = filtros.estado.length > 0 || filtros.categoria || filtros.prioridad || filtros.fechaDesde || filtros.fechaHasta || filtros.busqueda || filtros.filtroComunidad;

  return (
    <div className="incidencias-container">
      <div className="incidencias-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
            Incidencias
          </h1>
          <p>Gestiona y consulta el estado de las incidencias de tu comunidad</p>
        </div>
        {user.rol !== 'proveedor' && (
          <Button variant="primary" size="md" className="incidencias-primary-cta" onClick={() => setShowModal(true)}>
            + Nueva Incidencia
          </Button>
        )}
      </div>

      <div className="incidencias-search-wrapper">
        <div className="incidencias-search">
          <span className="incidencias-search-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>
          <input
            type="text"
            className="incidencias-search-input"
            placeholder="Buscar incidencias..."
            value={filtros.busqueda}
            onChange={(e) => updateFiltros({ ...filtros, busqueda: e.target.value })}
          />
        </div>
      </div>

      <div className="incidencias-filters-bar">
        <div className="incidencias-filter-tabs">
          <button
            className={`incidencias-filter-tab ${filtros.estado.length === 0 && !filtros.busqueda && !filtros.categoria.length && !filtros.prioridad.length && !filtros.fechaDesde && !filtros.fechaHasta ? 'active' : ''}`}
            onClick={() => {
              sessionStorage.removeItem('inc_filtros');
              sessionStorage.removeItem('inc_page');
              sessionStorage.removeItem('inc_advanced');
              updateFiltros({ estado: [], categoria: '', prioridad: '', fechaDesde: '', fechaHasta: '', busqueda: '', filtroComunidad: '' });
              setShowAdvancedFilters(false);
            }}
          >
            Todas
          </button>
          <button
            className={`incidencias-filter-tab ${filtros.estado.includes('pendiente') && filtros.estado.length === 1 ? 'active' : ''}`}
            onClick={() => updateFiltros({ ...filtros, estado: filtros.estado.includes('pendiente') ? [] : ['pendiente'] })}
          >
            Pendientes
          </button>
          <button
            className={`incidencias-filter-tab ${filtros.estado.includes('en_proceso') && filtros.estado.length === 1 ? 'active' : ''}`}
            onClick={() => updateFiltros({ ...filtros, estado: filtros.estado.includes('en_proceso') ? [] : ['en_proceso'] })}
          >
            En Proceso
          </button>
          <button
            className={`incidencias-filter-tab ${filtros.estado.includes('resuelta') && filtros.estado.length === 1 ? 'active' : ''}`}
            onClick={() => updateFiltros({ ...filtros, estado: filtros.estado.includes('resuelta') ? [] : ['resuelta'] })}
          >
            Resueltas
          </button>

          <button
            className="incidencias-filter-advanced-toggle"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            {showAdvancedFilters ? 'Básico' : 'Más filtros'}
          </button>
        </div>

        {showAdvancedFilters && (
          <div className="incidencias-advanced-filters">
            <div className="incidencias-filter-dates">
              <div className="incidencias-filter-group">
                <label>Categoría</label>
                <select
                  className="form-control"
                  value={filtros.categoria}
                  onChange={(e) => updateFiltros({ ...filtros, categoria: e.target.value })}
                >
                  <option value="">Todas</option>
                  {['Fontanería', 'Electricidad', 'Ascensor', 'Limpieza', 'Seguridad', 'Otros'].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="incidencias-filter-group">
                <label>Prioridad</label>
                <select
                  className="form-control"
                  value={filtros.prioridad}
                  onChange={(e) => updateFiltros({ ...filtros, prioridad: e.target.value })}
                >
                  <option value="">Todas</option>
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
              </div>
              <div className="incidencias-filter-group">
                <label>Desde</label>
                <DateInput
                  value={filtros.fechaDesde}
                  onChange={(val) => updateFiltros({ ...filtros, fechaDesde: val })}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              <div className="incidencias-filter-group">
                <label>Hasta</label>
                <DateInput
                  value={filtros.fechaHasta}
                  onChange={(val) => updateFiltros({ ...filtros, fechaHasta: val })}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              {user?.rol === 'administrador' && comunidades.length > 0 && (
                <div className="incidencias-filter-group">
                  <label>Comunidad</label>
                  <select
                    className="form-control"
                    value={filtros.filtroComunidad}
                    onChange={(e) => updateFiltros({ ...filtros, filtroComunidad: e.target.value })}
                  >
                    <option value="">Todas</option>
                    {comunidades.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {tieneFiltrosActivos && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  sessionStorage.removeItem('inc_filtros');
                  sessionStorage.removeItem('inc_page');
                  sessionStorage.removeItem('inc_advanced');
                  updateFiltros({ estado: [], categoria: '', prioridad: '', fechaDesde: '', fechaHasta: '', busqueda: '', filtroComunidad: '' });
                  setShowAdvancedFilters(false);
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="incidencias-feed-loading">
          {[1, 2, 3].map(i => (
            <div key={i} className="incidencias-post-skeleton"></div>
          ))}
        </div>
      ) : incidencias.length === 0 ? (
        <EmptyState
          icon="🎉"
          title={tieneFiltrosActivos ? 'No se encontraron resultados' : 'No tienes incidencias abiertas'}
          message={tieneFiltrosActivos
            ? 'Intenta ajustar los filtros para ver más resultados'
            : 'Si ocurre algo en tu comunidad, puedes crear una nueva incidencia'}
          action={
            user.rol !== 'proveedor' && (
              <Button variant="primary" size="md" onClick={() => setShowModal(true)}>
                + Crear Incidencia
              </Button>
            )
          }
        />
      ) : (
        <>
          <div className="incidencias-feed">
            {incidencias.map(incidencia => {
              const badge = getEstadoBadge(incidencia.estado);
              return (
                <div
                  key={incidencia.id}
                  className="incidencias-post"
                  onClick={() => handlePostClick(incidencia)}
                  onKeyDown={(event) => handlePostKeyDown(event, incidencia)}
                  role="button"
                  tabIndex={0}
                >
                  <h3 className="incidencias-post-title">{incidencia.titulo}</h3>

                  <div className="incidencias-post-meta">
                    <span className={`incidencias-post-estado incidencias-post-estado--${incidencia.estado || 'otro'}`}>
                      {badge.text}
                    </span>
                    {incidencia.prioridad && (
                      <span className={`incidencias-prioridad-badge incidencias-prioridad-badge--${incidencia.prioridad}`}>
                        {incidencia.prioridad}
                      </span>
                    )}
                    {incidencia.categoria && (
                      <span className="incidencias-categoria-badge">
                        {getCategoriaIcon(incidencia.categoria)}
                        {incidencia.categoria}
                      </span>
                    )}
                    {incidencia.visible_comunidad && (
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: 'rgba(34,197,94,0.12)', color: '#16a34a', fontWeight: '600' }}>
                        🏢 Comunidad
                      </span>
                    )}
                    <span className="incidencias-post-date">
                      {new Date(incidencia.fecha_creacion).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                  <p className="incidencias-post-description">{incidencia.descripcion}</p>

                  {(incidencia.creador_nombre || incidencia.comunidad_nombre) && (
                    <div className="incidencias-post-author">
                      👤 {[incidencia.creador_nombre, incidencia.comunidad_nombre].filter(Boolean).join(' · ')}
                    </div>
                  )}

                  <div className="incidencias-post-actions">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePostClick(incidencia);
                      }}
                    >
                      Ver detalles
                    </Button>
                    {puedeEliminar(incidencia) && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => handleEliminar(e, incidencia)}
                        style={{ flexShrink: 0 }}
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={total}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            />
          )}
        </>
      )}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '70px', paddingBottom: '20px', overflowY: 'auto', zIndex: 2000, backdropFilter: 'blur(8px)' }} onClick={handleCloseModal}>
          <div style={{ background: '#ffffff', borderRadius: 'var(--radius-xl)', padding: 'var(--space-6)', maxWidth: '560px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontSize: 'var(--font-2xl)', fontWeight: '700', margin: 0 }}>🔧 Nueva Incidencia</h2>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✖️</button>
            </div>
            <form onSubmit={handleSubmitIncidencia}>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Título *</label>
                <input type="text" name="titulo" className="form-control" value={newFormData.titulo} onChange={handleNewFormChange} required placeholder="Ej: Fuga de agua en portal 2" />
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Descripción *</label>
                <textarea name="descripcion" className="form-control" value={newFormData.descripcion} onChange={handleNewFormChange} required rows="4" placeholder="Describe la incidencia con detalle..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: 'var(--space-4)' }}>
                <div>
                  <label className="form-label">Categoría</label>
                  <select name="categoria" className="form-control" value={newFormData.categoria} onChange={handleNewFormChange}>
                    <option value="Fontanería">Fontanería</option>
                    <option value="Electricidad">Electricidad</option>
                    <option value="Ascensor">Ascensor</option>
                    <option value="Limpieza">Limpieza</option>
                    <option value="Seguridad">Seguridad</option>
                    <option value="Otros">Otros</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Prioridad</label>
                  <select name="prioridad" className="form-control" value={newFormData.prioridad} onChange={handleNewFormChange}>
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                  </select>
                </div>
              </div>
              {user?.rol === 'administrador' && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label">Comunidad *</label>
                  <select name="comunidad_id" className="form-control" value={newFormData.comunidad_id} onChange={handleNewFormChange} required>
                    <option value="">Selecciona una comunidad</option>
                    {comunidades.map(com => (<option key={com.id} value={com.id}>{com.nombre}</option>))}
                  </select>
                </div>
              )}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Ubicación</label>
                <input type="text" name="ubicacion" className="form-control" value={newFormData.ubicacion} onChange={handleNewFormChange} placeholder="Ej: Portal 2, 3º piso" />
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Imágenes (máx. 5)</label>
                {imagenes.length < 5 && (
                  <div style={{ marginBottom: '12px' }}>
                    <FileDropZone onFilesSelected={handleFilesSelected} maxFiles={5 - imagenes.length} multiple={true} />
                  </div>
                )}
                {previewUrls.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                    {previewUrls.map((url, index) => (
                      <div key={index} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '2px solid var(--border-color)' }}>
                        <img src={url} alt={`Preview ${index + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover' }} />
                        <button type="button" onClick={() => removeImage(index)} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(239,68,68,0.9)', color: 'white', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button variant="primary" type="submit" disabled={loadingSave} style={{ flex: 1 }}>{loadingSave ? 'Creando...' : '🔧 Crear Incidencia'}</Button>
                <Button variant="ghost" onClick={handleCloseModal} style={{ flex: 1 }}>Cancelar</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Incidencias;
